import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { User, CreditCard, Users } from "lucide-react";
import timezones from "./timezones.json";
import { Combobox } from "@/components/ui/combobox/combobox.component";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const MENU = [
  {
    key: "profile",
    label: "Profile",
    enabled: true,
    icon: <User className="w-5 h-5 mr-2" />,
  },
  {
    key: "billing",
    label: "Billing",
    enabled: false,
    icon: <CreditCard className="w-5 h-5 mr-2" />,
  },
  {
    key: "team",
    label: "Team",
    enabled: false,
    icon: <Users className="w-5 h-5 mr-2" />,
  },
];

interface SpaceSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPage?: "profile" | "billing" | "team";
}

export function SpaceSettingsDialog({
  open,
  onOpenChange,
  initialPage = "profile",
}: SpaceSettingsDialogProps) {
  const [page, setPage] = useState(initialPage);

  React.useEffect(() => {
    if (open) setPage(initialPage);
  }, [open, initialPage]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-row p-0 max-w-2xl">
        {/* Sidebar */}
        <div className="w-48 border-r h-full min-h-[inherit] flex flex-col bg-background-sidebar py-4 px-2 gap-1">
          {MENU.map((item) => (
            <Button
              key={item.key}
              variant={page === item.key ? "default" : "ghost"}
              className={`rounded-sm flex items-center gap-2 px-4 py-3 text-left text-base font-medium transition-all ${
                page === item.key
                  ? "bg-foreground/10 text-foreground"
                  : "text-foreground/60 hover:bg-foreground/5 hover:text-foreground/85"
              }`}
              disabled={!item.enabled}
              onClick={() =>
                item.enabled &&
                setPage(item.key as "profile" | "billing" | "team")
              }
            >
              {item.icon}
              <span>{item.label}</span>
            </Button>
          ))}
        </div>
        {/* Content */}
        <div className="flex-1 p-8">
          {page === "profile" && <ProfileSettings />}
          {page === "billing" && <DisabledPage label="Billing" />}
          {page === "team" && <DisabledPage label="Team" />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function getTimezoneOffsetLabel(tz: string) {
  try {
    const now = dayjs();
    const offset = now.tz(tz).utcOffset();
    const sign = offset >= 0 ? "+" : "-";
    const absOffset = Math.abs(offset);
    const hours = Math.floor(absOffset / 60)
      .toString()
      .padStart(2, "0");
    const minutes = (absOffset % 60).toString().padStart(2, "0");
    return `UTC${sign}${hours}${
      minutes === "00" ? "" : ":" + minutes
    } ${tz.replace(/_/g, " ")}`;
  } catch {
    return tz;
  }
}

function isTimezoneObj(
  tz: string | { utc: string[]; text: string }
): tz is { utc: string[]; text: string } {
  return (
    typeof tz === "object" &&
    tz !== null &&
    Array.isArray((tz as { utc: string[]; text: string }).utc) &&
    typeof (tz as { utc: string[]; text: string }).text === "string"
  );
}

function ProfileSettings() {
  const [selectedTimezone, setSelectedTimezone] = React.useState<string>("");
  const timezoneOptions = React.useMemo(
    () =>
      (timezones as (string | { utc: string[]; text: string })[])
        .map((tz) => {
          if (typeof tz === "string") {
            return {
              value: tz,
              label: getTimezoneOffsetLabel(tz),
            };
          } else if (isTimezoneObj(tz) && tz.utc.length > 0) {
            return {
              value: tz.utc[0],
              label: `${tz.text} (${tz.utc[0]})`,
            };
          } else {
            return undefined;
          }
        })
        .filter((x): x is { value: string; label: string } => Boolean(x)),
    []
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>Profile</DialogTitle>
      </DialogHeader>
      <div className="mt-6 space-y-4">
        <div>
          <label className="block mb-1 font-medium">Space Name</label>
          <input
            className="input input-bordered w-full"
            placeholder="Change space name..."
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">Timezone</label>
          <Combobox
            items={timezoneOptions}
            onSelect={setSelectedTimezone}
            placeholder="Select timezone..."
            size="M"
            query={selectedTimezone}
          />
        </div>
        <Button className="mt-4">Save Changes</Button>
      </div>
    </>
  );
}

function DisabledPage({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
      <h2 className="text-xl font-semibold mb-2">{label}</h2>
      <p>Coming soon</p>
    </div>
  );
}
